// Content for /system-types (mockup page 5).
//
// Everything here is a claim about how the three tiers of equipment behave,
// which is checkable engineering, not marketing: a single-stage compressor
// really is either on or off, and that really is why it struggles with
// humidity. Nothing here asserts a price, a rating, or a warranty.

export type TierId = 'single' | 'variable' | 'communicating'

export interface Tier {
    id: TierId
    ribbon: 'GOOD' | 'BETTER' | 'BEST'
    name: string
    tagline: string
    points: string[]
    bestFor: string
    /** Relative upfront cost, not a price. Three tiers, three symbols. */
    cost: '$' | '$$' | '$$$'
}

export const TIERS: Tier[] = [
    {
        id: 'single',
        ribbon: 'GOOD',
        name: 'Single-Stage Systems',
        tagline: 'Reliable. Affordable. Straightforward.',
        points: [
            'One speed: full output or off',
            'Basic humidity removal',
            'Lowest upfront cost',
            'Simple, proven technology',
            'Parts on most service vans',
        ],
        bestFor: 'Homes on a budget, or where comfort is already fine and the old unit simply died.',
        cost: '$',
    },
    {
        id: 'variable',
        ribbon: 'BETTER',
        name: 'Variable-Speed Systems',
        tagline: 'Better comfort. Better efficiency.',
        points: [
            'Compressor modulates instead of cycling',
            'Much better humidity control',
            'Quieter — it rarely runs flat out',
            'Lower running cost',
            'More consistent room-to-room temperatures',
        ],
        bestFor: 'Homeowners with rooms that never settle, or air that is cool but clammy.',
        cost: '$$',
    },
    {
        id: 'communicating',
        ribbon: 'BEST',
        name: 'Communicating Variable-Capacity Systems',
        tagline: 'Ultimate comfort. Maximum efficiency.',
        points: [
            'Modulates output precisely',
            'Best humidity and temperature control',
            'Whisper-quiet at low capacity',
            'Highest efficiency ratings available',
            'Components report faults to each other',
        ],
        bestFor: 'Homeowners staying put who want the best comfort and efficiency on offer.',
        cost: '$$$',
    },
]

export interface FeatureRow {
    feature: string
    /** 1–5, relative to the other two tiers only. */
    scores: [number, number, number]
    note?: string
}

export const FEATURES: FeatureRow[] = [
    { feature: 'Temperature consistency', scores: [2, 4, 5] },
    { feature: 'Humidity control', scores: [2, 4, 5] },
    { feature: 'Energy efficiency', scores: [2, 4, 5] },
    { feature: 'Operating sound', scores: [3, 4, 5] },
    {
        feature: 'Equipment longevity',
        scores: [3, 4, 4],
        note: 'Gentler cycling helps; more electronics is more that can fail. The install matters more than the tier.',
    },
    {
        feature: 'Simplicity to repair',
        scores: [5, 3, 2],
        note: 'The higher tiers are more specialist. Ask who services them locally before you buy.',
    },
]

/**
 * Per-tier installed cost: DECIDED AGAINST, not merely pending.
 *
 * The mockup printed ranges ($3,800 – $6,500 and so on) under a "Real
 * Prices. Real Data." banner; there was never a dataset behind them. The
 * open-items review (item 1) settled the question for good: no
 * tier-by-tier and no tonnage-by-tonnage pricing table anywhere on the
 * site. A homeowner needs the size of the decision and the reasons the
 * number moves — both of which live on /cost-guides as one broad planning
 * range — not a false precision that only a site visit could justify.
 *
 * So these cards show relative cost ($ / $$ / $$$) and link to the
 * planning range. This constant stays as documentation of the decision;
 * setting it would contradict the sign-off.
 */
export const TYPICAL_INSTALLED_COST: Record<TierId, { low: number; high: number }> | null = null
